import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const PROJECTS = [
  {
    id: "01",
    title: "Oz Oils",
    tagline: "Waste Oil Collection Operations",
    description:
      "Australian waste oil collection was running on Verizon, phone calls, and clipboards — drivers had no mobile app, the office could not track trucks, and rescheduling meant hours of phone tag. Oz Oils replaces that with a single operations platform: Google-optimised daily routes, live GPS tracking on a central map, a mobile driver panel with one-tap navigation, and a customer self-service reschedule portal. A callout system handles urgent same-day pickups without breaking recurring schedules.",
    role: "Software Engineer",
    stack: ["Vite", "React", "FastAPI", "PostgreSQL"],
    image: "/assets/portfolio/oz-oils.png",
    year: "2026",
  },
  {
    id: "02",
    title: "CopyFlo",
    tagline: "AI Content Engine",
    description:
      "Content teams spend more time on research and revision cycles than actual writing — the gap between 'we need a post' and 'content is published' is measured in days. CopyFlo compresses that pipeline into minutes. It ingests industry news from RSS and Google News, turns articles into AI-generated topic briefs, then produces finished content across five formats — social posts, blog articles, emails, ad copy, and video scripts — through a guided six-step wizard. Generation runs async via Celery, with credit-based billing and AI image generation for post visuals.",
    role: "Software Engineer",
    stack: ["Next.js", "FastAPI", "PostgreSQL", "Celery"],
    image: "/assets/portfolio/copyflo-ideas.png",
    year: "2026",
  },
  {
    id: "03",
    title: "Kuli Foto",
    tagline: "AI Headshot Studio",
    description:
      "Headshot studio without the studio. Upload a selfie, pick one of seven modes — corporate, executive, passport, and more — and get a professional photo back in 30–60 seconds. Try a watermarked demo first, pay only when you like the result, starting at Rp 200 per headshot.",
    role: "Founder / Full-stack",
    stack: ["Next.js", "Gemini AI", "Midtrans"],
    image: "/assets/portfolio/kuli-foto.png",
    year: "2026",
    href: "https://kulifoto.biz.id",
  },
  {
    id: "04",
    title: "Nano Banana Flow",
    tagline: "Visual AI Pipeline Builder",
    description:
      "A node-based canvas for turning messy image-generation experiments into reusable pipelines. Connect Input → Prompt → Output, hit Run, and Gemini does the work. Workflows are clonable, autosaved, and rate-limited per device.",
    role: "Design Engineer",
    stack: ["React Flow", "Gemini", "Zustand"],
    image: "/assets/portfolio/nanobananaflow.png",
    year: "2025",
  },
  {
    id: "05",
    title: "osce.ai",
    tagline: "AI OSCE Simulator for Med Students",
    description:
      "Medical students rarely get enough OSCE practice — standardised patients and examiners are scarce. osce.ai gives them an AI patient to interview, real-time assessment across seven clinical domains, a replay studio, and microskills coaching. Already used by ~400 students across three Indonesian universities.",
    role: "Medical Lead / Builder",
    stack: ["Laravel", "Gemini / Azure", "WebSockets"],
    image: "/assets/portfolio/osce-ai.png",
    year: "2025",
  },
  {
    id: "06",
    title: "AnnieDefect",
    tagline: "AI Building Facade Inspector",
    description:
      "Property surveyors in Australia typically spend days identifying facade defects, estimating materials and assembling reports. Upload two photos and an AI agent does the run — defect, severity, remediation steps, plus live material prices from Bunnings and regional labour rates, all packaged into a downloadable A4 PDF.",
    role: "Product Engineer",
    stack: ["OpenAI Agents", "FastAPI", "ReportLab"],
    image: "/assets/portfolio/anniedefect.png",
    year: "2025",
  },
];

export default function Projects() {
  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative py-24 md:py-32 border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-20">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C09D64]">
              (03) — Selected Work
            </p>
          </div>
          <div className="lg:col-span-8">
            <h2
              data-testid="projects-heading"
              className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.05] text-white text-balance"
            >
              Things built to{" "}
              <span className="italic text-zinc-300">return time</span> —{" "}
              <br className="hidden md:block" />
              not consume it.
            </h2>
            <p className="mt-6 text-zinc-500 text-sm md:text-base max-w-xl">
              A small shelf of products, tools and experiments — most living
              somewhere between medicine and machines.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {PROJECTS.map((p, i) => {
            const Wrapper = p.href ? "a" : "article";
            const wrapperProps = p.href
              ? {
                  href: p.href,
                  target: "_blank",
                  rel: "noopener noreferrer",
                }
              : {};
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <Wrapper
                  {...wrapperProps}
                  data-testid={`project-card-${i}`}
                  className="group relative block overflow-hidden rounded-2xl border border-white/[0.08] bg-[#050608] hover:border-white/20 transition-all duration-500"
                >
                  {/* Cover */}
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#0A0B10]">
                    {p.image && (
                      <img
                        src={p.image}
                        alt={`${p.title} preview`}
                        loading="lazy"
                        data-testid={`project-card-${i}-image`}
                        className="absolute inset-0 w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-[1200ms] ease-out"
                      />
                    )}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#050608] via-[#050608]/30 to-transparent" />
                    {/* Top meta on cover */}
                    <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-zinc-200">
                      <span
                        data-testid={`project-card-${i}-id`}
                        className="bg-black/40 backdrop-blur-md rounded-full px-2.5 py-1 border border-white/10"
                      >
                        № {p.id}
                      </span>
                      <span className="bg-black/40 backdrop-blur-md rounded-full px-2.5 py-1 border border-white/10">
                        {p.year}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-7 md:p-8">
                    <div className="flex items-start justify-between gap-6">
                      <div>
                        <h3
                          data-testid={`project-card-${i}-title`}
                          className="font-serif text-2xl md:text-3xl text-white leading-tight"
                        >
                          {p.title}
                        </h3>
                        <p className="mt-1 text-xs uppercase tracking-[0.22em] text-zinc-500">
                          {p.tagline}
                        </p>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-zinc-500 group-hover:text-[#C09D64] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-500 shrink-0 mt-1" />
                    </div>

                    <p
                      data-testid={`project-card-${i}-description`}
                      className="mt-4 text-sm md:text-[15px] text-zinc-400 leading-relaxed"
                    >
                      {p.description}
                    </p>

                    {/* Role + stack */}
                    <div className="mt-6 flex flex-wrap items-center gap-2">
                      <span
                        data-testid={`project-card-${i}-role`}
                        className="inline-flex items-center text-[10px] uppercase tracking-[0.22em] text-black bg-[#C09D64] rounded-full px-3 py-1.5 font-medium"
                      >
                        {p.role}
                      </span>
                      {p.stack.map((s) => (
                        <span
                          key={s}
                          className="inline-flex items-center text-[10px] uppercase tracking-[0.22em] text-zinc-300 border border-white/15 rounded-full px-3 py-1.5"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </Wrapper>
              </motion.div>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="mt-12 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-zinc-600">
          <span className="h-px flex-1 bg-white/[0.08]" />
          <span data-testid="projects-footer-note">More on the way</span>
          <span className="h-px flex-1 bg-white/[0.08]" />
        </div>
      </div>
    </section>
  );
}
